import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';

const PullToRefresh = ({ children, onRefresh }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef(null);
  const startY = useRef(0);
  const threshold = 120; // Distance to trigger refresh

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      // Only allow pull to refresh if we're at the top of the container
      if (window.scrollY <= 0) {
        startY.current = e.touches[0].pageY;
      } else {
        startY.current = 0;
      }
    };

    const handleTouchMove = (e) => {
      if (startY.current === 0 || isRefreshing) return;

      const currentY = e.touches[0].pageY;
      const distance = currentY - startY.current;

      if (distance > 0) {
        // Slow down pulling as it gets further
        const dampenedDistance = Math.min(distance * 0.4, threshold + 20);
        setPullDistance(dampenedDistance);
        
        // Prevent default scrolling when pulling
        if (distance > 10) {
          if (e.cancelable) e.preventDefault();
        }
      }
    };

    const handleTouchEnd = () => {
      if (pullDistance >= threshold && !isRefreshing) {
        triggerRefresh();
      } else {
        setPullDistance(0);
      }
      startY.current = 0;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pullDistance, isRefreshing]);

  const triggerRefresh = () => {
    setIsRefreshing(true);
    setPullDistance(threshold);
    
    if (onRefresh) {
      onRefresh();
    } else {
        // Default behavior: reload page
        setTimeout(() => {
            window.location.reload();
        }, 800);
    }
  };

  return (
    <div ref={containerRef} className="relative min-h-full">
      {/* Pull Indicator */}
      <div 
        className="absolute left-0 right-0 flex justify-center pointer-events-none z-50 transition-transform duration-200"
        style={{ 
          transform: `translateY(${pullDistance - 40}px)`,
          opacity: pullDistance > 20 ? 1 : 0
        }}
      >
        <div className={`bg-white rounded-full p-2 shadow-md border border-stone-100 ${isRefreshing ? 'animate-spin' : ''}`}>
          <RefreshCw 
            size={20} 
            className="text-[#022c22]" 
            style={{ 
              transform: isRefreshing ? 'none' : `rotate(${pullDistance * 2}deg)`,
              transition: isRefreshing ? 'none' : 'transform 0.1s linear'
            }} 
          />
        </div>
      </div>

      {/* Content */}
      <div 
        className="transition-transform duration-200"
        style={{ transform: `translateY(${isRefreshing ? 60 : pullDistance * 0.5}px)` }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
